import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { TrendingUp, Visibility, Edit } from '@mui/icons-material';

const InstallationStatus = () => {
  const [filters, setFilters] = useState({ stage: '', project: '' });
  const stages = useMemo(() => ([
    'Lead Generation', 'Site Survey', 'Quotation', 'Order Confirmed', 'Installation Planning', 'Installation', 'Quality Check', 'Commissioning', 'Handover'
  ]), []);

  const technicians = useMemo(() => ([
    { id: 'T-001', name: 'Amit Kumar', phone: '+91 90000 00001' },
    { id: 'T-002', name: 'Riya Sharma', phone: '+91 90000 00002' },
    { id: 'T-003', name: 'Vikram Singh', phone: '+91 90000 00003' },
  ]), []);

  const [rows, setRows] = useState([
    {
      ref: 'INS-1023',
      customerId: 'C-010',
      customerName: 'Ramesh Kumar',
      project: 'Rooftop A1',
      serviceType: 'Solar Panel',
      technicianId: 'T-001',
      technicianName: 'Amit Kumar',
      technicianPhone: '+91 90000 00001',
      date: '2025-10-30',
      stage: 'In Progress',
      currentIndex: 5,
      address: '221B Eco Colony, Jaipur',
      updatedBy: 'Admin',
      remarks: '',
    },
    {
      ref: 'INS-1024',
      customerId: 'C-011',
      customerName: 'Priya Sharma',
      project: 'Ground C2',
      serviceType: 'EV Charger',
      technicianId: 'T-002',
      technicianName: 'Riya Sharma',
      technicianPhone: '+91 90000 00002',
      date: '2025-10-31',
      stage: 'Assigned',
      currentIndex: 4,
      address: '12 Green Park, Pune',
      updatedBy: 'Admin',
      remarks: '',
    },
  ]);

  const filtered = rows.filter(r => (!filters.stage || r.stage === filters.stage) && (!filters.project || r.project.toLowerCase().includes(filters.project.toLowerCase())));

  const [open, setOpen] = useState(false);
  const [viewOnly, setViewOnly] = useState(false);
  const [form, setForm] = useState({
    ref: '',
    customerName: '',
    customerId: '',
    project: '',
    serviceType: '',
    technicianId: '',
    technicianName: '',
    technicianPhone: '',
    date: '',
    stage: 'Pending',
    address: '',
    remarks: '',
    updatedBy: 'Admin',
  });

  const openDialog = (row, isView = false) => {
    setForm({
      ref: row.ref,
      customerName: row.customerName,
      customerId: row.customerId || '',
      project: row.project,
      serviceType: row.serviceType || '',
      technicianId: row.technicianId || '',
      technicianName: row.technicianName || '',
      technicianPhone: row.technicianPhone || '',
      date: row.date || '',
      stage: row.stage,
      address: row.address || '',
      remarks: row.remarks || '',
      updatedBy: 'Admin',
    });
    setViewOnly(isView);
    setOpen(true);
  };

  const saveUpdate = () => {
    setRows(prev => prev.map(r => r.ref === form.ref ? {
      ...r,
      customerName: form.customerName,
      customerId: form.customerId,
      project: form.project,
      serviceType: form.serviceType,
      technicianId: form.technicianId,
      technicianName: form.technicianName,
      technicianPhone: form.technicianPhone,
      date: form.date,
      stage: form.stage,
      address: form.address,
      remarks: form.remarks,
      updatedBy: form.updatedBy,
    } : r));
    setOpen(false);
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Box sx={{ mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>Installation Status</Typography>
            <Typography variant="body2" color="text.secondary">Track installation progress by workflow stage</Typography>
          </Box>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Project" value={filters.project} onChange={(e) => setFilters(prev => ({ ...prev, project: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Stage</InputLabel>
                <Select value={filters.stage} label="Stage" onChange={(e) => setFilters(prev => ({ ...prev, stage: e.target.value }))}>
                  <MenuItem value="">All</MenuItem>
                  {stages.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Summary & Stepper preview for first row */}
      {filtered[0] && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <TrendingUp color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Workflow Preview • {filtered[0].project}</Typography>
            </Box>
            <Stepper activeStep={filtered[0].currentIndex} alternativeLabel>
              {stages.map(s => (
                <Step key={s}><StepLabel>{s}</StepLabel></Step>
              ))}
            </Stepper>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#536DFE' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Ref</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Project</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Customer</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Stage</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Assigned</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Date</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, textAlign: 'center' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(r => (
                  <TableRow key={r.ref} hover>
                    <TableCell sx={{ fontWeight: 700 }}>{r.ref}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{r.project}</TableCell>
                    <TableCell>{r.customerName}</TableCell>
                    <TableCell><Chip label={r.stage} size="small" color="primary" variant="outlined" /></TableCell>
                    <TableCell>{r.technicianName}</TableCell>
                    <TableCell>{r.date}</TableCell>
                    <TableCell align="center">
                      <Button size="small" startIcon={<Visibility />} onClick={() => openDialog(r, true)}></Button>
                      <Button size="small" startIcon={<Edit />} onClick={() => openDialog(r, false)}></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* View/Update Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Installation Status • {form.ref}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Customer Name" value={form.customerName} InputProps={{ readOnly: true }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Customer ID" value={form.customerId} InputProps={{ readOnly: true }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Installation ID" value={form.ref} InputProps={{ readOnly: true }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Product / Service Type" value={form.serviceType} onChange={(e) => setForm(prev => ({ ...prev, serviceType: e.target.value }))} disabled={viewOnly} />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Technician</InputLabel>
                <Select value={form.technicianId} label="Technician" onChange={(e) => {
                  const tech = technicians.find(t => t.id === e.target.value);
                  setForm(prev => ({ ...prev, technicianId: tech?.id || '', technicianName: tech?.name || '', technicianPhone: tech?.phone || '' }));
                }} disabled={viewOnly}>
                  <MenuItem value="">Select</MenuItem>
                  {technicians.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Technician Contact" value={form.technicianPhone} InputProps={{ readOnly: true }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" type="date" label="Installation Date" InputLabelProps={{ shrink: true }} value={form.date} onChange={(e) => setForm(prev => ({ ...prev, date: e.target.value }))} disabled={viewOnly} />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Installation Status</InputLabel>
                <Select value={form.stage} label="Installation Status" onChange={(e) => setForm(prev => ({ ...prev, stage: e.target.value }))} disabled={viewOnly}>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Assigned">Assigned</MenuItem>
                  <MenuItem value="In Progress">In Progress</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                  <MenuItem value="Cancelled">Cancelled</MenuItem>
                  <MenuItem value="Service Required">Service Required</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Location / Address" value={form.address} onChange={(e) => setForm(prev => ({ ...prev, address: e.target.value }))} disabled={viewOnly} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" multiline rows={3} label="Remarks / Notes" value={form.remarks} onChange={(e) => setForm(prev => ({ ...prev, remarks: e.target.value }))} disabled={viewOnly} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Updated By" value={form.updatedBy} InputProps={{ readOnly: true }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
          {!viewOnly && (
            <Button variant="contained" onClick={saveUpdate} sx={{ backgroundColor: '#536DFE', '&:hover': { backgroundColor: '#3F51B5' } }}>Update</Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InstallationStatus;



